import { ApolloQueryResult } from '@apollo/client';
import '@ionic/react/css/ionic-swiper.css';
import { isNil, isNumber } from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import 'swiper/css/navigation';
import Swiper from 'swiper';
import { FreeMode, Keyboard, Navigation, Thumbs, Zoom } from 'swiper/modules';
// Direct React component imports since create react app doesn't support pure ESM packages https://swiperjs.com/react#usage-with-create-react-app
import 'swiper/css';
import { Swiper as SwiperReact, SwiperSlide } from 'swiper/react';
import {
  Attachment,
  AttachmentSourceType,
  ListAttachmentsQuery,
  useListAttachmentsQuery,
} from '../../api/graphql/generated';
import { ImageResolution } from '../../api/hooks/attachment.hooks';
import { useListQueryWithInfiniteScroll } from '../../api/hooks/infinite-scroll.hooks';
import { trackLaunchedAttachmentViewer } from '../../config/analytics';
import { AttachmentsMetadata } from './attachment-modal';
import { AttachmentPreview } from './attachment-preview';
import './attachment-viewer.css';

interface AttachmentSelectedFunction {
  (attachment: Attachment, meta?: AttachmentsMetadata): void;
}

interface AttachmentViewerProps {
  source: AttachmentSourceType;
  sourceId: string | undefined;
  initialAttachmentId?: string;
  onSelected?: AttachmentSelectedFunction;
}

interface AttachmentViewerOptions {
  source: AttachmentSourceType;
  sourceId: string | undefined;
  initialAttachmentId?: string;
  onSelected?: AttachmentSelectedFunction;
}

const THUMB_ACTIVE_CLASS = 'thumb-active';

export const useAttachmentViewer = (options: AttachmentViewerOptions) => {
  const { source, sourceId, initialAttachmentId, onSelected } = options;

  const attachmentViewer = useMemo(() => {
    return (
      <AttachmentViewer
        source={source}
        sourceId={sourceId}
        initialAttachmentId={initialAttachmentId}
        onSelected={onSelected}
      />
    );
  }, [source, sourceId, initialAttachmentId, onSelected]);

  return attachmentViewer;
};

export const AttachmentViewer = (
  props: AttachmentViewerProps
): ReactElement => {
  const { source, sourceId, initialAttachmentId, onSelected } = props;

  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);

  // by default, swiperjs jumps to the last active slide when the infinite scroll retrieves more attachments
  // this effect sets the swiper to the correct index after fetching more data
  const scrollToStartOfNextItems = useCallback(
    (result: ApolloQueryResult<ListAttachmentsQuery>) => {
      const { items, total } = result.data.listAttachments;
      if (!isNil(items) && isNumber(total)) {
        const index = total - items.length;
        thumbsSwiper?.slideTo(index);
      }
    },
    [thumbsSwiper]
  );

  const {
    infiniteScroll,
    items,
    totalItems,
    result: { loading },
  } = useListQueryWithInfiniteScroll({
    useQuery: useListAttachmentsQuery,
    input: {
      source,
      sourceId,
    },
    queryOptions: {
      skip: sourceId === undefined,
      fetchPolicy: 'cache-and-network',
    },
    axis: 'horizontal',
    onGetMore: scrollToStartOfNextItems,
  });

  const hasMore = items.length < totalItems;
  const attachments = items as Attachment[];
  const initialSlide: number | undefined = useMemo(() => {
    if (initialAttachmentId === undefined) {
      return;
    }

    const index = attachments.findIndex(
      (attachment) => attachment.id === initialAttachmentId
    );
    return index > -1 ? index : undefined;
  }, [attachments, initialAttachmentId]);

  const [activeIndex, setActiveIndex] = useState<number>(initialSlide ?? 0);

  useEffect(() => {
    trackLaunchedAttachmentViewer();
  }, []);

  useEffect(() => {
    if (
      typeof onSelected === 'function' &&
      attachments[activeIndex] !== undefined
    ) {
      onSelected(attachments[activeIndex], {
        currentAttachment: activeIndex + 1,
        totalAttachments: totalItems,
      });
    }
  }, [activeIndex, attachments, onSelected, totalItems]);

  const swiperZoomSlides = attachments.map((attachment) => {
    return (
      <SwiperSlide key={attachment.id}>
        <div className="swiper-zoom-container">
          <AttachmentPreview
            attachment={attachment}
            resolution={ImageResolution.HIGHRES}
            isSmallPreview={false}
          />
        </div>
      </SwiperSlide>
    );
  });

  const swiperThumbSlides = attachments.map((attachment, index) => {
    const isActive = activeIndex === index;
    return (
      <SwiperSlide
        key={attachment.id}
        className={isActive ? THUMB_ACTIVE_CLASS : undefined}
      >
        <AttachmentPreview
          attachment={attachment}
          resolution={ImageResolution.THUBMNAIL}
          isSmallPreview={true}
        />
      </SwiperSlide>
    );
  });

  return (
    <>
      <SwiperReact
        className="main-swiper"
        initialSlide={initialSlide}
        modules={[FreeMode, Navigation, Thumbs, Zoom, Keyboard]}
        keyboard={{
          enabled: true,
          pageUpDown: true,
        }}
        navigation={true}
        pagination={{ clickable: true }}
        thumbs={{
          swiper: thumbsSwiper,
        }}
        zoom={true}
        onActiveIndexChange={(swiper) => {
          setActiveIndex(swiper.activeIndex);
        }}
      >
        {swiperZoomSlides}
        {infiniteScroll && hasMore && (
          <SwiperSlide>{infiniteScroll}</SwiperSlide>
        )}
      </SwiperReact>
      <SwiperReact
        className="secondary-swiper"
        noSwiping={loading}
        onSwiper={(swiper) => setThumbsSwiper(swiper)}
        initialSlide={initialSlide}
        spaceBetween={2}
        slidesPerView={4}
        breakpoints={{
          500: {
            slidesPerView: 6,
          },
          1000: {
            slidesPerView: 10,
          },
        }}
        centeredSlides={true}
        centeredSlidesBounds={true}
        freeMode={true}
        watchSlidesProgress={true}
        modules={[FreeMode, Navigation, Thumbs]}
      >
        {swiperThumbSlides}
        {infiniteScroll && hasMore && (
          <SwiperSlide>{infiniteScroll}</SwiperSlide>
        )}
      </SwiperReact>
    </>
  );
};
